import classNames from 'classnames';
import { type JSX, type RefObject } from 'react';

import { Tooltip } from '@videoblocks/design-system';
import { View, ViewOff } from '@videoblocks/react-icons';

interface PasswordInputProps {
    inputRef: RefObject<HTMLInputElement | null>;
    error: string | undefined;
    touched: boolean;
    showPassword: boolean;
    onTogglePassword: () => void;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onFocus: () => void;
    onBlur: () => void;
}

const INPUT_CLASS =
    'w-full border rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 placeholder:text-gray-700';

export function PasswordInput({
    inputRef,
    error,
    touched,
    showPassword,
    onTogglePassword,
    onChange,
    onFocus,
    onBlur,
}: PasswordInputProps): JSX.Element {
    return (
        <div className="mb-4">
            <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-1">
                Password
            </label>
            <div className="relative">
                <input
                    ref={inputRef}
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    required
                    className={classNames('pr-10', INPUT_CLASS, {
                        'border-gray-700': !error || !touched,
                        'border-red-500': error && touched,
                    })}
                    placeholder="Enter password"
                    autoComplete="new-password"
                    minLength={6}
                    onChange={onChange}
                    onFocus={onFocus}
                    onBlur={onBlur}
                />
                <Tooltip
                    title={showPassword ? 'Hide password' : 'Show password'}
                    trigger={
                        <button
                            type="button"
                            onClick={onTogglePassword}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    onTogglePassword();
                                }
                            }}
                            className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset focus:text-gray-600"
                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                            tabIndex={0}
                        >
                            {showPassword ? <ViewOff className="h-4 w-4" /> : <View className="h-4 w-4" />}
                        </button>
                    }
                />
            </div>
            {touched && error && <div className="mt-1 text-sm text-red-600">{error}</div>}
        </div>
    );
}
